import { useMemo } from 'react';
import {
  Bouton, IconeCroix, IconePlus, IconeProgramme, SaisieDifferee, useColonnes,
} from '@journal/ui';

/**
 * Tableau croisé de la programmation.
 *
 * Reprend la structure de `Programmation.loadTable` de l'application d'origine :
 * une colonne par PÉRIODE, une ligne par SOUS-DOMAINE, et dans chaque case les
 * blocs de contenu.
 *
 * ── Pourquoi ce croisement et pas une liste ─────────────────────────────────
 * Programmer une année consiste à répartir des notions sur cinq périodes. La
 * question posée est « qu'est-ce que je fais de la géométrie au deuxième
 * trimestre ? » : elle a deux entrées, et se lit à l'intersection. Une liste à
 * plat oblige à reconstruire ce croisement de tête, ce qui est précisément le
 * travail qu'on veut éviter.
 *
 * ── L'en-tête de période porte les semaines ─────────────────────────────────
 * Le nombre de semaines est ce qui dit si la période est tenable. Le voir en
 * haut de colonne pendant qu'on remplit évite de s'apercevoir en mars qu'on a
 * prévu douze séquences pour six semaines.
 */

export interface ColonnePeriode {
  id: string;
  nom: string;
  numero: number;
  debut: string;
  fin: string;
  semaines: number;
}

export interface LigneSousDomaine {
  /** `null` pour la ligne « Général », qui recueille les blocs non rattachés. */
  id: string | null;
  nom: string;
  /**
   * TOUS les identifiants de domaine portant ce nom.
   *
   * Le référentiel répète un domaine par niveau : une classe de CM1-CM2 a deux
   * « Lecture ». La ligne en montre le contenu quel que soit celui sous lequel
   * il a été saisi — sinon la moitié du travail disparaîtrait de l'écran au
   * simple ajout d'un niveau à la classe.
   */
  ids: string[];
}

export interface BlocCellule {
  id: string;
  curriculumDomainId: string | null;
  termId: string;
  content: string;
  source: 'libre' | 'programme';
}

const dateCourte = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'short', timeZone: 'UTC',
  });

/**
 * Blocs indexés par cellule.
 *
 * Sans cet index, chaque case rebalayait la liste ENTIÈRE des blocs :
 * quarante sous-domaines × cinq périodes × deux cents blocs font quarante
 * mille comparaisons par rendu — et le rendu se refait à chaque frappe dans
 * n'importe quelle case. Un parcours unique remplace le produit.
 */
export function indexerBlocs(blocs: BlocCellule[]): Map<string, BlocCellule[]> {
  const index = new Map<string, BlocCellule[]>();
  for (const b of blocs) {
    const cle = `${b.curriculumDomainId ?? ''}|${b.termId}`;
    const liste = index.get(cle);
    if (liste) liste.push(b); else index.set(cle, [b]);
  }
  return index;
}

/** Blocs d'une case : la ligne rassemble tous ses identifiants de domaine. */
export function blocsDeLaCase(
  index: Map<string, BlocCellule[]>,
  ligne: LigneSousDomaine,
  periodeId: string,
): BlocCellule[] {
  const cles = ligne.id === null ? [''] : ligne.ids;
  return cles.flatMap((id) => index.get(`${id}|${periodeId}`) ?? []);
}

export function TableauProgrammation({
  periodes, sousDomaines, blocs,
  surAjoutLibre, surAjoutProgramme, surModification, surSuppression,
}: {
  periodes: ColonnePeriode[];
  sousDomaines: LigneSousDomaine[];
  blocs: BlocCellule[];
  surAjoutLibre: (ligne: LigneSousDomaine, periodeId: string) => void;
  /*
   * La LIGNE entière, et pas son seul identifiant : le sélecteur du programme
   * doit ne proposer que les objectifs de ce domaine — donc de tous les
   * identifiants qui portent son nom, un par niveau de la classe.
   */
  surAjoutProgramme: (ligne: LigneSousDomaine, periodeId: string) => void;
  surModification: (bloc: BlocCellule, contenu: string) => Promise<boolean>;
  surSuppression: (bloc: BlocCellule) => void;
}) {
  const parCellule = useMemo(() => indexerBlocs(blocs), [blocs]);
  // Les periodes n'ont pas toutes le meme volume de contenu : on regle.
  const colonnes = useColonnes('programmation');

  return (
    <div
      role="region"
      aria-label="Programmation par sous-domaine et par période"
      tabIndex={0}
      data-impression="programmation"
      className="impression-paysage overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--ombre-carte)] focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
    >
      <table className="w-full border-collapse text-sm">
        {colonnes.colgroup(['Sous-domaine', ...periodes.map((p) => p.id)])}
        <caption className="sr-only">
          Sous-domaines en lignes, périodes en colonnes. Chaque case contient les
          blocs programmés.
        </caption>
        <thead>
          <tr>
            {/* Première colonne collante : sans elle, on perd le nom du
                sous-domaine dès qu'on défile vers la cinquième période. */}
            <th
              scope="col"
              // `relative` : la poignée se place en absolu sur le bord droit
              // de SA cellule, et n'avait ici aucun repère.
              className="relative sticky left-0 z-10 min-w-[11rem] border-b border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]"
              style={colonnes.entete('Sous-domaine').style}
            >
              Sous-domaine
              {colonnes.poignee('Sous-domaine')}
            </th>
            {periodes.map((p) => (
              <th
                key={p.id}
                scope="col"
                className="relative min-w-[13rem] border-b border-l border-[var(--color-border)] bg-[var(--color-surface-raised)] px-3 py-2 text-center align-top"
                style={colonnes.entete(p.nom).style}
              >
                {colonnes.poignee(p.nom)}
                <span className="block text-[13px] font-semibold">{p.nom}</span>
                <span className="block text-[11px] font-normal text-[var(--color-fg-muted)]">
                  {`${p.semaines} semaine${p.semaines > 1 ? 's' : ''}`}
                </span>
                <span className="block text-[10px] font-normal text-[var(--color-fg-muted)]">
                  {`${dateCourte(p.debut)} – ${dateCourte(p.fin)}`}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sousDomaines.map((sd) => (
            <tr key={sd.id ?? 'general'} className="border-t border-[var(--color-border)]">
              <th
                scope="row"
                /*
                 * Sans barre de couleur : la teinte de la matière est la même
                 * sur toutes les lignes du tableau, elle n'y distingue rien.
                 * Voir l'en-tête de `TableauProgressions`.
                 */
                className="sticky left-0 z-10 bg-[var(--color-surface)] px-3 py-2 text-left align-top text-[13px] font-medium"
              >
                {sd.nom}
              </th>

              {periodes.map((periode) => {
                const dedans = blocsDeLaCase(parCellule, sd, periode.id);
                return (
                  <td
                    key={periode.id}
                    className="border-l border-[var(--color-border)] p-1.5 align-top"
                  >
                    <div className="flex flex-col gap-1.5">
                      {dedans.map((bloc) => (
                        /*
                          * Le × est posé dans le COIN, par-dessus la bulle :
                          * il occupait une ligne à lui seul, et le texte
                          * commençait sous un grand vide — même pour un bloc
                          * libre, qui n'a pas d'étiquette à côté de lui.
                          */
                        <div
                          key={bloc.id}
                          className="relative rounded-md border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-1 pr-7"
                        >
                          {bloc.source === 'programme' && (
                            <span
                              className="block px-2 pt-0.5 text-[9px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]"
                              title="Issu du programme officiel"
                            >
                              Programme
                            </span>
                          )}
                          <Bouton
                            variante="ghost"
                            taille="sm"
                            iconeSeule
                            icone={<IconeCroix taille={12} />}
                            onClick={() => surSuppression(bloc)}
                            aria-label={`Supprimer « ${bloc.content || 'bloc vide'} »`}
                            className="absolute right-0.5 top-0.5 size-6"
                          >
                            Supprimer
                          </Bouton>
                          {/*
                            * Saisie directe dans la case, comme l'original :
                            * on programme une période en remplissant vingt
                            * cases à la suite, sans dialogue entre chacune.
                            */}
                          <SaisieDifferee
                            libelle={`Bloc · ${sd.nom} · ${periode.nom}`}
                            valeur={bloc.content}
                            surEnregistrement={(v) => surModification(bloc, v)}
                          />
                        </div>
                      ))}

                      <div className="flex gap-1">
                        <Bouton
                          variante="ghost"
                          taille="sm"
                          icone={<IconePlus taille={13} />}
                          onClick={() => surAjoutLibre(sd, periode.id)}
                          aria-label={`Ajouter un bloc libre · ${sd.nom} · ${periode.nom}`}
                          className="flex-1 border border-dashed border-[var(--color-border-strong)]"
                        >
                          Libre
                        </Bouton>
                        <Bouton
                          variante="ghost"
                          taille="sm"
                          icone={<IconeProgramme taille={13} />}
                          onClick={() => surAjoutProgramme(sd, periode.id)}
                          aria-label={`Ajouter depuis le programme · ${sd.nom} · ${periode.nom}`}
                          className="flex-1 border border-dashed border-[var(--color-primary)] text-[var(--color-primary)]"
                        >
                          Programme
                        </Bouton>
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
